import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { img } from "../../constants/assets";
import { login } from "../../services/apiService";
import { getDashboardHomePath } from "../../utils/auth";
import LanguageSwitcher from "../../components/LanguageSwitcher";

function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = location.state?.from || getDashboardHomePath();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: null, message: "" });

    const formData = new FormData(event.target);
    const payload = {
      email: formData.get("email")?.trim(),
      password: formData.get("password"),
    };

    setIsSubmitting(true);

    try {
      const response = await login(payload);
      const storage = rememberMe ? localStorage : sessionStorage;

      if (response) {
        storage.setItem("loginResponse", JSON.stringify(response));
      }

      if (response?.data) {
        storage.setItem("user", JSON.stringify(response.data));
      }

      navigate(redirectPath);
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          t("auth.loginFailed"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-lang-switcher-wrap">
        <LanguageSwitcher variant="auth" />
      </div>
      <div className="auth-card">
        <div className="auth-form-panel">
          <div className="auth-form-inner">
            <Link className="auth-logo" to="/">
              <img src={img("logo.svg")} alt="R4L Solutions" />
            </Link>

            <h1 className="auth-title">{t("auth.login")}</h1>
            <p className="auth-subtitle">{t("auth.loginSubtitle")}</p>

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="auth-field">
                <input
                  id="login-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder=" "
                  required
                />
                <label htmlFor="login-email">{t("common.email")}</label>
              </div>

              <div className="auth-field auth-field--password">
                <input
                  id="login-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder=" "
                  required
                />
                <label htmlFor="login-password">{t("auth.password")}</label>
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={
                    showPassword ? t("auth.hidePassword") : t("auth.showPassword")
                  }
                >
                  {showPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="3"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M3 3l18 18"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M10.58 10.58A3 3 0 0 0 12 15a3 3 0 0 0 2.42-4.42M9.88 4.24A10.94 10.94 0 0 1 12 4c6.5 0 10 7 10 7a18.36 18.36 0 0 1-2.16 3.19M6.61 6.61A18.5 18.5 0 0 0 2 12s3.5 7 10 7a9.86 9.86 0 0 0 4.39-1"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </button>
              </div>

              <div className="auth-form-options">
                <label className="auth-checkbox">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(event) => setRememberMe(event.target.checked)}
                  />
                  <span>{t("auth.rememberMe")}</span>
                </label>
                <Link className="auth-link" to="/forgot-password">
                  {t("auth.forgotPassword")}
                </Link>
              </div>

              <button type="submit" className="auth-submit" disabled={isSubmitting}>
                {isSubmitting ? t("auth.loggingIn") : t("auth.login")}
              </button>

              {status.message ? (
                <p
                  className={`auth-form-status auth-form-status--${status.type}`}
                  role="alert"
                >
                  {status.message}
                </p>
              ) : null}
            </form>
          </div>
        </div>

        <div className="auth-image-panel">
          <div className="auth-image-wrap">
            <img
              src={img("loginRegister.png")}
              alt={t("auth.authImageAlt")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
